// Gmail-style rich body editor. Wraps a contentEditable div so users can
// paste images that land INLINE in the body (not as a chip below).
//
// Why not a library like Tiptap / Lexical: those are 100KB+ and have their
// own learning curve. For our use case (paste text + paste images +
// optional slash menu insertion via imperative ref) a thin wrapper around
// contentEditable does the job in <200 lines.
//
// State model:
//   - The contentEditable's innerHTML is the source of truth while typing.
//   - `value` (HTML string) is the React mirror, kept in sync via onInput.
//   - When the parent wants to programmatically set the content (Draft /
//     Polish / template insertion / restore from localStorage), it calls
//     `editorRef.current.setHtml(html)` or `insertAtCaret(node)` — never
//     by changing the `value` prop, which would clobber the caret.
//
// What this editor does NOT try to do:
//   - WYSIWYG toolbars (bold/italic/lists). Plain prose + inline images is
//     all we need; richer formatting is a Tiptap-grade lift.
//   - Drag-to-resize images. Uses CSS max-width:100% so they always fit;
//     users can re-paste a smaller version if they need to.
//   - Rich HTML paste sanitization. We deliberately strip incoming rich
//     HTML to plain text — clipboard from web pages brings junk styles
//     that fight email rendering.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ClipboardEvent as ReactClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { cn } from '@/lib/utils';
import { sanitizePastedHtml } from '@/lib/html-normalize';

export interface RichBodyEditorHandle {
  /** Replace the entire body. Used for Draft / Polish / template apply / discard. */
  setHtml: (html: string) => void;
  /** Insert plain text at the caret (or replace selection). */
  insertText: (text: string) => void;
  /** Insert an HTML fragment at the caret. */
  insertHtml: (html: string) => void;
  /** Focus the editor and place caret at the end. */
  focusEnd: () => void;
  /** Read current HTML content. */
  getHtml: () => string;
  /** Read plain-text equivalent (innerText). Useful for AI / validation. */
  getText: () => string;
  /** Get the underlying contentEditable element. */
  getElement: () => HTMLDivElement | null;
}

interface Props {
  /** Initial HTML — only read at mount. After that the editor is uncontrolled.
   *  Use the imperative ref methods to update content programmatically. */
  initialHtml?: string;
  /** Called on every input with the editor's current innerHTML. */
  onChange?: (html: string) => void;
  /** Called when the user pastes file(s). Image files are inserted inline
   *  before this fires (already handled); other file types arrive here so
   *  the parent can route them to the regular attachments cluster. */
  onPasteFiles?: (files: File[]) => void;
  /** Forwarded to the contentEditable div. Useful for Cmd+Enter, slash
   *  menu trigger, etc. */
  onKeyDown?: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
  /** Hint string shown when the editor is empty. Standard contentEditable
   *  doesn't have a placeholder; we render one via a CSS pseudo-element
   *  and toggle a data-empty attribute based on innerText length. */
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

export const RichBodyEditor = forwardRef<RichBodyEditorHandle, Props>(function RichBodyEditor(
  { initialHtml = '', onChange, onPasteFiles, onKeyDown, placeholder, className, ariaLabel },
  ref,
) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  // Tracks the last HTML we set programmatically, so we don't fire a
  // redundant onChange when the parent calls setHtml.
  const lastSetRef = useRef<string>(initialHtml);

  // Mount: hydrate the contentEditable from initialHtml. We use a ref-based
  // initial paint instead of dangerouslySetInnerHTML because React would
  // re-render and clobber the contentEditable's internal state on subsequent
  // updates.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = initialHtml;
      updateEmptyState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateEmptyState() {
    const el = editorRef.current;
    if (!el) return;
    const isEmpty = el.innerText.trim().length === 0 && el.querySelectorAll('img').length === 0;
    el.dataset.empty = isEmpty ? 'true' : 'false';
  }

  useImperativeHandle(ref, () => ({
    setHtml(html: string) {
      const el = editorRef.current;
      if (!el) return;
      el.innerHTML = html;
      lastSetRef.current = html;
      updateEmptyState();
      // Fire onChange so React state mirrors the new content.
      onChange?.(html);
    },
    insertText(text: string) {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      // execCommand is technically deprecated but it's still the simplest
      // way to insert at caret in contentEditable across browsers. The
      // modern Selection-API alternative is also more brittle for our
      // needs (caret restoration after insert).
      document.execCommand('insertText', false, text);
      onChange?.(el.innerHTML);
      updateEmptyState();
    },
    insertHtml(html: string) {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      document.execCommand('insertHTML', false, html);
      onChange?.(el.innerHTML);
      updateEmptyState();
    },
    focusEnd() {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    },
    getHtml() {
      return editorRef.current?.innerHTML ?? '';
    },
    getText() {
      return editorRef.current?.innerText ?? '';
    },
    getElement() {
      return editorRef.current;
    },
  }));

  function handleInput() {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    if (html !== lastSetRef.current) {
      lastSetRef.current = html;
      onChange?.(html);
    }
    updateEmptyState();
  }

  function handlePaste(e: ReactClipboardEvent<HTMLDivElement>) {
    const cd = e.clipboardData;
    if (!cd) return;

    // Files first (images + everything else). Images get inlined directly
    // into the editor; non-image files bubble up to onPasteFiles so the
    // parent can add them to the regular attachments cluster.
    const files: File[] = [];
    for (const item of Array.from(cd.items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    const otherFiles = files.filter((f) => !f.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      e.preventDefault();
      // Insert each image as an `<img src="blob:..." data-pa-attach-id="...">`
      // at the caret. The blob URL renders immediately. On send the parent
      // walks the HTML, uploads each blob, and rewrites src to the
      // permanent public URL.
      for (const file of imageFiles) {
        const blobUrl = URL.createObjectURL(file);
        // Per-image local id so the parent can correlate img tags with the
        // File objects it needs to upload.
        const localId = crypto.randomUUID();
        // Stash the file on a window-scoped registry so the send pipeline
        // can resolve localId → File. This is module-internal; see
        // exports below.
        pendingInlineImages.set(localId, file);
        const safeAlt = file.name.replace(/"/g, '&quot;');
        const html =
          `<img src="${blobUrl}" data-pa-attach-id="${localId}" alt="${safeAlt}" ` +
          `style="max-width:100%;height:auto;display:block;margin:8px 0;border-radius:4px;" />`;
        document.execCommand('insertHTML', false, html);
      }
      handleInput();
      // Still surface non-image files to the parent if any.
      if (otherFiles.length > 0) onPasteFiles?.(otherFiles);
      return;
    }

    if (otherFiles.length > 0) {
      e.preventDefault();
      onPasteFiles?.(otherFiles);
      return;
    }

    // Rich-text paste. Preserve links + basic formatting — sanitised down to
    // a safe subset (see sanitizePastedHtml) — so URLs copied from Slack /
    // Gmail / another editor stay clickable instead of being flattened to
    // plain text. Falls back to plain text when there's no usable HTML.
    const html = cd.getData('text/html');
    const text = cd.getData('text/plain');
    if (html && html.trim()) {
      const clean = sanitizePastedHtml(html);
      if (clean.trim()) {
        e.preventDefault();
        document.execCommand('insertHTML', false, clean);
        handleInput();
        return;
      }
    }
    if (text) {
      e.preventDefault();
      document.execCommand('insertText', false, text);
      handleInput();
    }
    // If nothing matched, let the default paste behavior run.
  }

  return (
    <div
      ref={editorRef}
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onPaste={handlePaste}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      data-empty="true"
      className={cn(
        // Base text styling — match the surrounding composer (Tailwind
        // prose-style minimums). No max-height: the editor grows with
        // its content, and because the composer is no longer sticky,
        // the whole page just scrolls. This matches Gmail's behaviour.
        'min-h-[8.25rem] resize-none border-0 bg-transparent px-3.5 py-3 text-sm leading-relaxed outline-none',
        // Inline images render at natural size up to the editor width.
        // No hard height cap — the page scrolls, so a tall paste is
        // fine. Underlying image sent to the recipient is untouched.
        '[&_img]:max-w-full [&_img]:h-auto [&_img]:my-2 [&_img]:rounded',
        // Toolbar-inserted formatting — render with visible styles so the
        // composer view matches what most email clients will show on the
        // recipient side. (Recipients also get default UA styling for
        // <blockquote>, <code>, <ul>, <ol>; we just make sure it looks
        // correct here too.)
        '[&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
        '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
        '[&_a]:text-tint-info-fg [&_a]:underline-offset-2 hover:[&_a]:underline',
        // Empty-state placeholder via CSS pseudo-element. The data-empty
        // attribute is toggled on input.
        'before:pointer-events-none before:text-muted-foreground before:content-[attr(data-placeholder)] data-[empty=false]:before:hidden',
        className,
      )}
      data-placeholder={placeholder}
    />
  );
});

// Module-internal registry: localId → File for inline-pasted images.
// The send pipeline reads from this when rewriting blob: src to public URL.
// Cleared on successful send (caller's responsibility — see
// `consumePendingImages` below).
const pendingInlineImages = new Map<string, File>();

/** Put images back in the registry. Used when a reply that was queued for
 *  sending returns to the composer: `consumePendingImages` emptied the
 *  registry when Send was clicked, so without this the restored body would hold
 *  `blob:` images with no File behind them. */
export function registerPendingImages(images: Map<string, File>): void {
  for (const [id, file] of images) pendingInlineImages.set(id, file);
}

/** Read + clear the pending inline-image registry. Call this in the send
 *  pipeline after extracting the relevant ids from the HTML. */
export function consumePendingImages(ids: string[]): Map<string, File> {
  const out = new Map<string, File>();
  for (const id of ids) {
    const f = pendingInlineImages.get(id);
    if (f) {
      out.set(id, f);
      pendingInlineImages.delete(id);
    }
  }
  return out;
}

// findInlineImageIds / rewriteInlineImageSrcs moved to lib/inline-images.ts so the
// send pipeline can use them without React. Re-exported so existing imports keep working.
export { findInlineImageIds, rewriteInlineImageSrcs } from '@/lib/inline-images';
